import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES_CONSOLE, type RoleConsole } from '@artosera/shared/permissions';
import { estErreurConfinement, moi } from '../lib/api.js';
import type { ApiError } from '../lib/api.js';
import { useConfinementSignale } from '../lib/confinement.js';
import type { Me } from '../types.js';

export const CLE_SESSION = ['auth', 'me'] as const;

/**
 * La session console. `erreurAuthentification` distingue « pas connecté »
 * (401, redirection vers /connexion) de toute autre panne (affichée telle quelle).
 *
 * `confine` : la session ne peut plus que s'enrôler au second facteur. `/me`
 * le dit (`enrolementMfaSeulement`), une autre route a pu le dire avant lui
 * (`lib/confinement.ts`), ou `/me` lui-même a été refusé pour ce motif — une
 * session confinée n'est pas une session absente.
 */
export function useSession(): {
  moi: Me | undefined;
  enChargement: boolean;
  erreurAuthentification: boolean;
  erreur: ApiError | null;
  confine: boolean;
} {
  const signale = useConfinementSignale();
  // Un refus DÉFINITIF de `/me` (401 : pas de session ; 403 : confinement)
  // n'est pas relancé à chaque montage : la garde remontait sinon en
  // « chargement », démontait l'écran, qui remontait, relançait… La connexion
  // et l'enrôlement invalident la session eux-mêmes. Une panne PASSAGÈRE (500,
  // réseau), elle, est relue au montage suivant — celui de la page de
  // connexion où la garde a mené : la session revient sans reconnexion.
  const erreurPrecedente = useQueryClient().getQueryState<Me, ApiError>(CLE_SESSION)?.error;
  const refusDefinitif = erreurPrecedente?.status === 401 || erreurPrecedente?.status === 403;
  const requete = useQuery<Me, ApiError>({
    queryKey: CLE_SESSION,
    queryFn: moi,
    retry: false,
    retryOnMount: !refusDefinitif,
    staleTime: 60_000,
  });

  return {
    moi: requete.data,
    enChargement: requete.isLoading,
    erreurAuthentification: requete.error?.status === 401,
    erreur: requete.error ?? null,
    confine:
      signale ||
      requete.data?.enrolementMfaSeulement === true ||
      estErreurConfinement(requete.error),
  };
}

export function estRoleConsole(role: string | null | undefined): role is RoleConsole {
  return role !== null && role !== undefined && (ROLES_CONSOLE as readonly string[]).includes(role);
}
